import { useState, useEffect } from "react";
import { admin, type AuditLogEntry } from "@/lib/api";
import Spinner from "@/components/Spinner";
import { ScrollText, ChevronLeft, ChevronRight, Download } from "lucide-react";
import clsx from "clsx";

const ACTION_COLORS: Record<string, string> = {
  user_updated:     "bg-blue-50 text-blue-700",
  user_disabled:    "bg-red-50 text-red-700",
  user_enabled:     "bg-green-50 text-green-700",
  usage_reset:      "bg-amber-50 text-amber-700",
  viewed_campaigns: "bg-purple-50 text-purple-700",
  viewed_users:     "bg-slate-50 text-slate-700",
};

const ACTIONS = ["", "user_updated", "user_disabled", "user_enabled", "usage_reset", "viewed_campaigns"];

export default function AdminAuditLogPage() {
  const [logs, setLogs]         = useState<AuditLogEntry[]>([]);
  const [total, setTotal]       = useState(0);
  const [loading, setLoading]   = useState(true);
  const [page, setPage]         = useState(1);
  const [actionFilter, setActionFilter] = useState("");
  const limit = 25;

  useEffect(() => {
    setLoading(true);
    admin.auditLog({ action: actionFilter || undefined, page, limit })
      .then((r) => { setLogs(r.logs); setTotal(r.total); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [page, actionFilter]);

  const totalPages = Math.ceil(total / limit);

  const exportCsv = () => {
    const rows = [["Time","Admin","Action","Target","IP"]];
    logs.forEach((l) => rows.push([
      new Date(l.created_at).toISOString(),
      l.admin_email, l.action,
      `${l.target_type}:${l.target_id || ""}`,
      l.ip_address || "",
    ]));
    const blob = new Blob([rows.map((r) => r.join(",")).join("\n")], { type: "text/csv" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob);
    a.download = `audit-log-page-${page}.csv`; a.click();
  };

  return (
    <div>
      <div className="sticky top-0 z-10 bg-paper/90 backdrop-blur-md border-b border-black/[0.06] px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
        <div>
          <h1 className="text-lg sm:text-xl font-bold text-ink">Audit Log</h1>
          <p className="text-xs sm:text-sm text-slate mt-0.5">{total.toLocaleString()} events recorded</p>
        </div>
        <button onClick={exportCsv}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-black/[0.08] text-sm font-semibold text-slate hover:border-black/[0.16] hover:text-ink transition-colors">
          <Download size={14} />
          <span className="hidden sm:inline">Export CSV</span>
        </button>
      </div>

      <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto space-y-5">
        {/* Action filter pills */}
        <div className="flex gap-1.5 flex-wrap">
          {ACTIONS.map((a) => (
            <button key={a || "all"} onClick={() => { setActionFilter(a); setPage(1); }}
              className={clsx("px-3 py-1.5 rounded-full text-xs font-semibold transition-all",
                actionFilter === a
                  ? "bg-ink text-white"
                  : "bg-white text-slate border border-black/[0.06] hover:border-black/[0.14]"
              )}>
              {a ? a.replace(/_/g, " ") : "All actions"}
            </button>
          ))}
        </div>

        {loading ? <Spinner className="py-16" /> : logs.length === 0 ? (
          <div className="bg-white rounded-2xl border border-black/[0.06] py-16 text-center">
            <ScrollText size={28} className="mx-auto mb-3 opacity-20" />
            <p className="text-slate text-sm font-medium">No audit events found.</p>
          </div>
        ) : (
          <>
            {/* Desktop table */}
            <div className="hidden sm:block bg-white rounded-2xl border border-black/[0.06] overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-black/[0.06]">
                      {["Time", "Admin", "Action", "Target", "Details", "IP"].map((h) => (
                        <th key={h} className="px-5 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-slate whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {logs.map((log) => (
                      <tr key={log.id} className="border-b border-black/[0.04] hover:bg-black/[0.01] transition-colors">
                        <td className="px-5 py-3.5 text-xs text-slate whitespace-nowrap">
                          {new Date(log.created_at).toLocaleString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                        </td>
                        <td className="px-5 py-3.5">
                          <p className="text-xs font-semibold text-ink">{log.admin_name}</p>
                          <p className="text-[10px] text-slate">{log.admin_email}</p>
                        </td>
                        <td className="px-5 py-3.5">
                          <span className={clsx("px-2.5 py-1 rounded-full text-[11px] font-semibold whitespace-nowrap",
                            ACTION_COLORS[log.action] || "bg-black/[0.04] text-slate")}>
                            {log.action.replace(/_/g, " ")}
                          </span>
                        </td>
                        <td className="px-5 py-3.5 text-xs text-slate">
                          <span className="font-medium">{log.target_type}</span>
                          {log.target_id && <span className="text-slate/50 font-mono ml-1 text-[10px]">:{log.target_id.slice(0, 8)}…</span>}
                        </td>
                        <td className="px-5 py-3.5 text-xs text-slate max-w-[200px] truncate">
                          {Object.keys(log.details || {}).length > 0 ? JSON.stringify(log.details) : "—"}
                        </td>
                        <td className="px-5 py-3.5 text-xs text-slate font-mono">{log.ip_address || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Mobile log list */}
            <div className="sm:hidden space-y-2">
              {logs.map((log) => (
                <div key={log.id} className="bg-white rounded-xl border border-black/[0.06] p-4">
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <span className={clsx("px-2.5 py-1 rounded-full text-[10px] font-semibold",
                      ACTION_COLORS[log.action] || "bg-black/[0.04] text-slate")}>
                      {log.action.replace(/_/g, " ")}
                    </span>
                    <span className="text-[10px] text-slate whitespace-nowrap">
                      {new Date(log.created_at).toLocaleString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                    </span>
                  </div>
                  <p className="text-xs font-semibold text-ink">{log.admin_name}</p>
                  <p className="text-[10px] text-slate mt-0.5">{log.target_type}{log.target_id ? ` · ${log.target_id.slice(0,8)}…` : ""}</p>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-3">
                <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page === 1}
                  className="p-2.5 rounded-xl border border-black/[0.08] disabled:opacity-30 hover:border-black/[0.16] transition-colors">
                  <ChevronLeft size={15} />
                </button>
                <span className="text-sm text-slate font-medium">
                  Page {page} of {totalPages}
                </span>
                <button onClick={() => setPage(Math.min(totalPages, page + 1))} disabled={page === totalPages}
                  className="p-2.5 rounded-xl border border-black/[0.08] disabled:opacity-30 hover:border-black/[0.16] transition-colors">
                  <ChevronRight size={15} />
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
